import { supabase } from '@/lib/supabase'
import type { RewardsAccountExchangeLink, RewardsExchange } from '../types'

export type RewardsMasterCycle = {
  id: string
  account_id: string
  period_start: string
  period_end: string
  status: 'open' | 'closed'
  created_at: string
  updated_at: string
}

export type RewardsMasterMetricRow = {
  cycle_id: string
  exchange: RewardsExchange
  volume_usdt: number
  commission_estimated_usdt: number
  commission_confirmed_usdt: number
  last_synced_at: string | null
  updated_at: string
}

const IDS_CHUNK_SIZE = 500

export async function listRewardsMasterLinks(params?: { limit?: number }): Promise<RewardsAccountExchangeLink[]> {
  const limit = clampInt(params?.limit ?? 2000, 1, 5000)
  const { data, error } = await supabase
    .from('rewards_account_exchange_links')
    .select('id,account_id,exchange,exchange_user_id,status,linked_at,last_sync_at,last_error,created_at,updated_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return ((data || []) as any[]).map((r) => ({
    id: r.id,
    account_id: r.account_id,
    exchange: r.exchange,
    exchange_user_id: r.exchange_user_id,
    status: r.status,
    linked_at: r.linked_at || null,
    last_sync_at: r.last_sync_at || null,
    last_error: r.last_error || null,
    created_at: r.created_at,
    updated_at: r.updated_at,
  }))
}

export async function listRewardsOpenCycles(accountIds: string[]): Promise<RewardsMasterCycle[]> {
  const ids = uniq(accountIds)
  if (!ids.length) return []
  const out: RewardsMasterCycle[] = []
  for (const part of chunk(ids, IDS_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('rewards_account_cycles')
      .select('id,account_id,period_start,period_end,status,created_at,updated_at')
      .in('account_id', part as any)
      .eq('status', 'open')
    if (error) throw error
    out.push(
      ...((data || []) as any[]).map((r) => ({
        id: r.id,
        account_id: r.account_id,
        period_start: r.period_start,
        period_end: r.period_end,
        status: r.status,
        created_at: r.created_at,
        updated_at: r.updated_at,
      })),
    )
  }
  return out
}

export async function listRewardsCyclesInRange(accountIds: string[], startIso: string): Promise<RewardsMasterCycle[]> {
  const ids = uniq(accountIds)
  if (!ids.length) return []
  const out: RewardsMasterCycle[] = []
  for (const part of chunk(ids, IDS_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('rewards_account_cycles')
      .select('id,account_id,period_start,period_end,status,created_at,updated_at')
      .in('account_id', part as any)
      .gte('period_start', startIso)
      .order('period_start', { ascending: true })
    if (error) throw error
    out.push(
      ...((data || []) as any[]).map((r) => ({
        id: r.id,
        account_id: r.account_id,
        period_start: r.period_start,
        period_end: r.period_end,
        status: r.status,
        created_at: r.created_at,
        updated_at: r.updated_at,
      })),
    )
  }
  return out
}

export async function listRewardsMetricsByCycleIds(cycleIds: string[]): Promise<RewardsMasterMetricRow[]> {
  const ids = uniq(cycleIds)
  if (!ids.length) return []
  const out: RewardsMasterMetricRow[] = []
  for (const part of chunk(ids, IDS_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('rewards_cycle_exchange_metrics')
      .select('cycle_id,exchange,volume_usdt,commission_estimated_usdt,commission_confirmed_usdt,last_synced_at,updated_at')
      .in('cycle_id', part as any)
    if (error) throw error
    out.push(
      ...((data || []) as any[]).map((r) => ({
        cycle_id: r.cycle_id,
        exchange: r.exchange,
        volume_usdt: Number(r.volume_usdt || 0),
        commission_estimated_usdt: Number(r.commission_estimated_usdt || 0),
        commission_confirmed_usdt: Number(r.commission_confirmed_usdt || 0),
        last_synced_at: r.last_synced_at || null,
        updated_at: r.updated_at,
      })),
    )
  }
  return out
}

function chunk<T>(items: T[], size: number): T[][] {
  const n = Math.max(1, Math.floor(size))
  const out: T[][] = []
  for (let i = 0; i < items.length; i += n) out.push(items.slice(i, i + n))
  return out
}

function uniq(items: string[]): string[] {
  return Array.from(new Set(items.map((v) => String(v || '').trim()).filter(Boolean)))
}

function clampInt(v: number, min: number, max: number): number {
  const n = Math.floor(Number(v))
  if (!Number.isFinite(n)) return min
  return Math.max(min, Math.min(max, n))
}
